import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserPayments } from "src/store/slices/chatSlice";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS } from "src/utils/EventConstants";
import { toast } from "react-toastify";

/**
 * Calls GET /api/user/payments once on startup after the user is authenticated.
 * Stores results in Redux (chat.userPayments) for downstream use.
 * Per-thread recovery (pendingPaymentConfirmations) is handled in ChatPageComponent.
 *
 * Also consumes userPayments to implement Mechanism 2 cross-thread watchdog:
 * - Tracks Payment Action Pending for unactioned complete payments
 * - Tracks Payment Action Timeout and shows user notification if >10 min elapsed
 * - Shows info toast with thread link for recent unactioned payments
 */
const PaymentRecoveryMonitor = () => {
  const dispatch = useDispatch();
  const fetchedRef = useRef(false);
  const processedRef = useRef(false);

  const userPayments = useSelector((state) => state.chat.userPayments);
  const userPaymentsStatus = useSelector((state) => state.chat.userPaymentsStatus);

  const userMetadataFetchStatus = useSelector((state) => state.onboarding.userMetadataFetchStatus);

  // Wait for getUserMetadata to succeed (valid token confirmed) before fetching payments
  useEffect(() => {
    if (userMetadataFetchStatus !== "success" || fetchedRef.current) return;
    fetchedRef.current = true;
    dispatch(getUserPayments());
  }, [userMetadataFetchStatus, dispatch]);

  useEffect(() => {
    if (userPaymentsStatus !== "success" || processedRef.current) return;
    processedRef.current = true;

    const unactionedPayments = userPayments.filter(
      (p) => p.payment_status === "complete" && p.action_taken === false,
    );

    unactionedPayments.forEach((payment) => {
      const { payment_id, payment_type, thread_id, completed_at } = payment;
      const minutesSinceCompleted = completed_at
        ? (Date.now() - new Date(completed_at).getTime()) / 60000
        : 0;

      trackAnalytics(EVENT_NAMES.PAYMENT_ACTION_PENDING, {
        [EVENT_PARAMETERS.PAYMENT_ID]: payment_id,
        [EVENT_PARAMETERS.THREAD_ID]: thread_id,
        payment_type,
        minutes_since_completed: Math.round(minutesSinceCompleted),
      });

      if (minutesSinceCompleted > 10) {
        trackAnalytics(EVENT_NAMES.PAYMENT_ACTION_TIMEOUT, {
          [EVENT_PARAMETERS.PAYMENT_ID]: payment_id,
          [EVENT_PARAMETERS.THREAD_ID]: thread_id,
          payment_type,
          minutes_elapsed: Math.round(minutesSinceCompleted),
        });
        toast.warn(
          "Your payment is confirmed but we're still processing. If this persists, contact support.",
          { toastId: `payment-timeout-${payment_id}` },
        );
      } else {
        toast.info(
          <span>
            Your recent payment is being processed —{" "}
            <a href={`/chat/${thread_id}`} style={{ textDecoration: "underline" }}>
              view your document
            </a>
          </span>,
          { toastId: `payment-pending-${payment_id}` },
        );
      }
    });
  }, [userPaymentsStatus, userPayments]);

  return null;
};

export default PaymentRecoveryMonitor;
